import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { trackUsageEvent } from '../usage-events';

interface Deal {
  qt_deal_id: string;
  name: string | null;
  amount: number | null;
  closedate: string | null;
  stage: string | null;
  stage_label: string | null;
  stage_color: string | null;
  pipeline: string | null;
  pipeline_label: string | null;
  prob: number | null;
  is_dup: boolean | null;
  last_contact_type: string | null;
  last_contact_date: string | null;
  contact_id: number | null;
  updated_by: string | null;
  qt_updated_at: string | null;
  archived: boolean | null;
}

type State =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; deals: Deal[] };

const QT_BASE_URL = 'https://quote-tracker-odb.onrender.com';

function formatCurrency(amount: number | null): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '—';
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatRelativeDate(iso: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '—';
  const now = Date.now();
  const diffMs = now - then.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day === 1) return 'yesterday';
  if (day < 7) return `${day}d ago`;
  return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function PipelineView(): JSX.Element {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [pipelineFilter, setPipelineFilter] = useState<string>('');
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const [expandedDealId, setExpandedDealId] = useState<string | null>(null);

  useEffect(() => {
    void trackUsageEvent('pipeline_opened', { route: '/pipeline' });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    const params = new URLSearchParams();
    if (pipelineFilter) params.set('pipeline', pipelineFilter);
    if (showArchived) params.set('archived', 'true');
    params.set('limit', '500');
    (async () => {
      try {
        const res = await fetch(`/api/deals?${params.toString()}`, { credentials: 'include' });
        if (cancelled) return;
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          setState({ kind: 'error', message: body.error ?? `Request failed: ${res.status}` });
          return;
        }
        const json = (await res.json()) as { deals: Deal[] };
        setState({ kind: 'ready', deals: json.deals });
      } catch (err) {
        if (cancelled) return;
        setState({ kind: 'error', message: (err as Error).message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pipelineFilter, showArchived]);

  const pipelineOptions = useMemo<string[]>(() => {
    if (state.kind !== 'ready') return [];
    const set = new Set<string>();
    for (const d of state.deals) {
      if (d.pipeline_label) set.add(d.pipeline_label);
    }
    return Array.from(set).sort();
  }, [state]);

  const grouped = useMemo<Array<{ stage: string; deals: Deal[] }>>(() => {
    if (state.kind !== 'ready') return [];
    const map = new Map<string, Deal[]>();
    for (const d of state.deals) {
      const key = d.stage_label ?? d.stage ?? '(no stage)';
      const arr = map.get(key) ?? [];
      arr.push(d);
      map.set(key, arr);
    }
    return Array.from(map.entries()).map(([stage, deals]) => ({ stage, deals }));
  }, [state]);

  return (
    <div className="max-w-full space-y-4 overflow-x-hidden">
      <div className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900 ring-1 ring-amber-200">
        <div className="font-semibold">Legacy mirror, not the action cockpit.</div>
        <p className="mt-1 leading-5">
          This page exists for inspection while HubSpot-era deal data is still being unwound. QT remains the place to
          work the deal; Odoo is the long-term record for quotes and orders.
        </p>
        <a
          href={QT_BASE_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            void trackUsageEvent('pipeline_deal_deeplink_clicked', {
              route: '/pipeline',
              entity_type: 'quote_tracker',
              entity_id: 'pipeline',
            });
          }}
          className="mt-2 inline-flex rounded-md bg-amber-100 px-2.5 py-1.5 text-xs font-semibold text-amber-950 ring-1 ring-amber-300 hover:bg-amber-200"
        >
          Open Workbench cockpit
        </a>
      </div>

      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-slate-900">Legacy pipeline mirror</h2>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-slate-200">
              Read-only
            </span>
          </div>
          <p className="mt-1 max-w-3xl text-sm text-slate-500">
            Mirrored deal records grouped by stage for lookup and context. Use the contact link for evidence and QT for
            deal work.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-xs text-slate-600 flex flex-col gap-1">
            Pipeline
            <select
              value={pipelineFilter}
              onChange={(e) => {
                const value = e.target.value;
                setPipelineFilter(value);
                void trackUsageEvent('pipeline_filter_changed', {
                  route: '/pipeline',
                  metadata: { filter: 'pipeline', value: value || 'all' },
                });
              }}
              className="rounded-md ring-1 ring-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 min-w-[10rem]"
            >
              <option value="">All</option>
              {pipelineOptions.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-slate-600 inline-flex items-center gap-2 pt-4">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => {
                setShowArchived(e.target.checked);
                void trackUsageEvent('pipeline_filter_changed', {
                  route: '/pipeline',
                  metadata: { filter: 'archived', value: e.target.checked },
                });
              }}
              className="rounded"
            />
            Show archived
          </label>
        </div>
      </header>

      {state.kind === 'loading' && (
        <div className="rounded-lg bg-white ring-1 ring-slate-200 p-6 text-sm text-slate-500">Loading deals…</div>
      )}
      {state.kind === 'error' && (
        <div className="rounded-lg bg-rose-50 ring-1 ring-rose-200 p-4 text-sm text-rose-700">
          Error: {state.message}
        </div>
      )}
      {state.kind === 'ready' && grouped.length === 0 && (
        <div className="rounded-lg bg-white ring-1 ring-slate-200 p-6 text-sm text-slate-500">No deals found.</div>
      )}
      {state.kind === 'ready' && grouped.length > 0 && (
        <div className="space-y-4">
          {grouped.map(({ stage, deals }) => {
            const total = deals.reduce((sum, d) => sum + (d.amount ?? 0), 0);
            return (
              <section key={stage} className="rounded-lg bg-white ring-1 ring-slate-200">
                <header className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-900">{stage}</h3>
                  <div className="text-xs text-slate-500">
                    {deals.length} {deals.length === 1 ? 'deal' : 'deals'} · {formatCurrency(total)}
                  </div>
                </header>
                <ul className="divide-y divide-slate-100">
                  {deals.map((d) => (
                    <li key={d.qt_deal_id} className="flex flex-col gap-3 px-4 py-3 md:flex-row md:items-start md:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          {d.contact_id !== null ? (
                            <Link
                              to={`/contacts/${d.contact_id}`}
                              onClick={() => {
                                void trackUsageEvent('pipeline_contact_opened', {
                                  route: '/pipeline',
                                  entity_type: 'contact',
                                  entity_id: d.contact_id,
                                  metadata: { qt_deal_id: d.qt_deal_id },
                                });
                              }}
                              className="text-sm font-medium text-slate-900 hover:underline truncate"
                            >
                              {d.name ?? '(unnamed deal)'}
                            </Link>
                          ) : (
                            <span className="text-sm font-medium text-slate-900 truncate">
                              {d.name ?? '(unnamed deal)'}
                            </span>
                          )}
                          {d.is_dup && (
                            <span className="inline-flex items-center rounded-full bg-rose-50 ring-1 ring-rose-200 px-2 py-0.5 text-xs text-rose-700">
                              dup
                            </span>
                          )}
                          {d.archived && (
                            <span className="inline-flex items-center rounded-full bg-slate-100 ring-1 ring-slate-300 px-2 py-0.5 text-xs text-slate-600">
                              archived
                            </span>
                          )}
                          {d.pipeline_label && (
                            <span className="text-xs text-slate-400">{d.pipeline_label}</span>
                          )}
                        </div>
                        <div className="mt-1 text-xs text-slate-500 flex items-center gap-3 flex-wrap">
                          <span className="font-medium text-slate-700">{formatCurrency(d.amount)}</span>
                          {d.last_contact_type && (
                            <span>
                              last {d.last_contact_type}
                              {d.last_contact_date ? ` · ${formatRelativeDate(d.last_contact_date)}` : ''}
                            </span>
                          )}
                          {d.updated_by && <span>by {d.updated_by}</span>}
                          {d.qt_updated_at && <span>updated {formatRelativeDate(d.qt_updated_at)}</span>}
                        </div>
                        {expandedDealId === d.qt_deal_id && (
                          <div className="mt-2 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600 ring-1 ring-slate-200">
                            <span className="font-medium text-slate-700">QT deal:</span> {d.qt_deal_id}
                            {d.stage_label ? <span> · Stage: {d.stage_label}</span> : null}
                            {d.pipeline_label ? <span> · Pipeline: {d.pipeline_label}</span> : null}
                          </div>
                        )}
                      </div>
                      <div className="flex shrink-0 flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setExpandedDealId((curr) => (curr === d.qt_deal_id ? null : d.qt_deal_id));
                            void trackUsageEvent('pipeline_deal_opened', {
                              route: '/pipeline',
                              entity_type: 'deal',
                              entity_id: d.qt_deal_id,
                            });
                          }}
                          className="text-xs text-slate-500 hover:text-slate-900 underline"
                        >
                          Details
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            void navigator.clipboard?.writeText(d.qt_deal_id);
                            void trackUsageEvent('pipeline_export_or_copy_clicked', {
                              route: '/pipeline',
                              entity_type: 'deal',
                              entity_id: d.qt_deal_id,
                              metadata: { action: 'copy_qt_deal_id' },
                            });
                          }}
                          className="text-xs text-slate-500 hover:text-slate-900 underline"
                        >
                          Copy ID
                        </button>
                        <a
                          href={`${QT_BASE_URL}/deals/${d.qt_deal_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => {
                            void trackUsageEvent('pipeline_deal_deeplink_clicked', {
                              route: '/pipeline',
                              entity_type: 'deal',
                              entity_id: d.qt_deal_id,
                            });
                          }}
                          className="rounded-md bg-slate-900 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-slate-800"
                        >
                          Open in QT
                        </a>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
